/// <amd-module name="Build/Admin/PoolsList/Components/CreateExistingPoolContainer" />
import * as React from "react";

import { TaskAgentPool, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { IProjectData } from "Tfs/Platform/Page";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { IButtonProps } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";

import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolsListActionCreator } from "Build/Admin/PoolsList/Actions/PoolsListActionCreator";
import { PipelinePermissionsSettings } from "Build/Admin/PoolsList/Components/PipelinePermissionsSettings";

import * as Resources from "Build/Admin/PoolsList/Resources";

export interface ICreateExistingPoolContainerProps {
    actionCreator: PoolsListActionCreator;
    existingPoolOptions: IListBoxItem<TaskAgentPool>[];
    onFooterButtonPropsChanged(footerButtonProps: IButtonProps[]): void;
    disableAllPipelinesSetting: boolean;
}

interface ICreateExistingPoolContainerState {
    authorizeAllPipelines: boolean;
    selectedExistingPool?: TaskAgentPool;
}

export class CreateExistingPoolContainer extends VssComponent<ICreateExistingPoolContainerProps, ICreateExistingPoolContainerState> {
    private _project: IProjectData | undefined;

    constructor(props: ICreateExistingPoolContainerProps, context: IVssComponentContext) {
        super(props, context);

        const projectDataService: IProjectDataService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        this._project = projectDataService.getProjectData();

        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.onFooterButtonPropsChanged(this._getFooterButtonProps());
    }

    public componentDidUpdate(prevProps: ICreateExistingPoolContainerProps, prevState: ICreateExistingPoolContainerState): void {
        if (prevState.selectedExistingPool !== this.state.selectedExistingPool) {
            this.props.onFooterButtonPropsChanged(this._getFooterButtonProps());
        }
    }

    public render(): JSX.Element {
        return (
            <div className="create-existing-pool-container">
                <Dropdown
                    className="existing-pool-dropdown"
                    disabled={this.props.existingPoolOptions.length === 0}
                    placeholder={Resources.SelectPoolLabel}
                    items={this.props.existingPoolOptions}
                    onSelect={this._onExistingPoolSelectionChanged} />
                <PipelinePermissionsSettings
                    disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                    authorizeAllPipelines={this.state.authorizeAllPipelines}
                    autoProvisionProjectPools={false}
                    showAutoProvisionOption={false}
                    showAuthorizeAllPipelinesOption
                    onSettingsChanged={this._onPermissionsChanged} />
            </div>
        );
    }

    private _onPermissionsChanged = (authorizeAllPipelines: boolean, autoProvisionProjectPools: boolean): void => {
        this.setState({
            authorizeAllPipelines
        });
    }

    private _onExistingPoolSelectionChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<TaskAgentPool>): void => {
        this.setState({
            selectedExistingPool: item.data
        });
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores = (): ICreateExistingPoolContainerState => {
        // This is used on panel initialization, and to reset the panel state on open/close
        // so some of the panel's fields are set to their default values below.
        // The panel's fields are set by setState calls in the child component callbacks in the render function.
        return {
            authorizeAllPipelines: false,
            selectedExistingPool: undefined
        };
    }

    private _okOnClick = (): void => {
        if (this._project && this.state.selectedExistingPool) {
            const newQueue: TaskAgentQueue = {
                name: this.state.selectedExistingPool.name,
                pool: {
                    id: this.state.selectedExistingPool.id
                }
            } as TaskAgentQueue;

            this.props.actionCreator.addAgentQueue(newQueue, this._project.id, this.state.authorizeAllPipelines);
            this.props.actionCreator.dismissCreatePoolPanel();
        }
    }

    private _shouldDisableSubmitButton = (): boolean => {
        return !this.state.selectedExistingPool;
    }

    private _getFooterButtonProps = (): IButtonProps[] => {
        return [
            {
                text: Resources.Create,
                primary: true,
                onClick: this._okOnClick,
                disabled: this._shouldDisableSubmitButton()
            }
        ];
    }
}
